export type Locale = "en" | "ar";

export interface ReportColumn { key: string; label: string; labelAr?: string | null; type: string }
export interface ReportResult {
  key: string;
  version: number;
  name: string;
  nameAr?: string | null;
  chart: "table" | "bar" | "line" | "pie" | "kpi" | string;
  columns: ReportColumn[];
  rows: unknown[][];
  sourceRows: number;
  generatedAt: string;
}

export interface RecordFieldOption { value: string; label: string; labelAr?: string | null }
export interface RecordField {
  key: string; label: string; labelAr?: string | null; type: string; required?: boolean; pattern?: string | null;
  min?: number | null; max?: number | null; maxLength?: number | null; options?: RecordFieldOption[] | null; target?: string | null;
  searchable?: boolean; listed?: boolean; help?: string | null; helpAr?: string | null;
}
export interface RecordRelation { key: string; label: string; labelAr?: string | null; target: string; many?: boolean; required?: boolean }
export interface RecordTypeView {
  key: string; version: number; name: string; nameAr?: string | null; description?: string | null; descriptionAr?: string | null; icon: string;
  numberPrefix: string; titleField?: string | null; departmentKey?: string | null; extendsServiceKey?: string | null; fields: RecordField[]; relations: RecordRelation[];
}
export interface DataRecord {
  id: string; recordTypeKey: string; recordTypeVersion: number; number: string; title: string; data: Record<string, unknown> | null;
  caseId?: string | null; departmentKey?: string | null; createdByName?: string | null; createdAt: string; updatedByName?: string | null; updatedAt: string;
  isTest: boolean; isArchived: boolean;
}
export interface RecordLinkView {
  id: string; relationKey: string; relationLabel?: string | null; relationLabelAr?: string | null; incoming: boolean;
  recordId?: string | null; recordNumber?: string | null; recordTitle?: string | null; recordTypeKey?: string | null; caseId?: string | null; caseNumber?: string | null; caseTitle?: string | null;
}
export interface RecordIssue { path: string; message: string; messageAr?: string | null; severity?: string }

/** One block of a screen, as the Screen Builder writes it into `ScreenDefinition.blocks`. */
export type ScreenBlock =
  | { type: "heading"; text: string; textAr?: string | null; body?: string | null; bodyAr?: string | null }
  | { type: "text"; text: string; textAr?: string | null }
  | { type: "recordList"; recordType: string; columns?: string[]; pageSize?: number; allowCreate?: boolean; title?: string | null; titleAr?: string | null }
  | { type: "recordForm"; recordType: string; fields?: string[]; submitLabel?: string | null; submitLabelAr?: string | null; title?: string | null; titleAr?: string | null }
  | { type: "recordDetail"; recordType: string; fields?: string[]; showLinks?: boolean }
  | { type: "report"; report: string; title?: string | null; titleAr?: string | null }
  | { type: "kpi"; report: string; label?: string | null; labelAr?: string | null; column?: string | null }
  | { type: "serviceStart"; service: string; label?: string | null; labelAr?: string | null; body?: string | null; bodyAr?: string | null }
  | { type: "caseList"; service: string; title?: string | null; titleAr?: string | null }
  | { type: "caseDetail"; showTimeline?: boolean; showTasks?: boolean; showDocuments?: boolean; showActions?: boolean };

export type ScreenBlockType = ScreenBlock["type"];
export const SCREEN_BLOCK_TYPES: ScreenBlockType[] = ["heading", "text", "recordList", "recordForm", "recordDetail", "report", "kpi", "serviceStart", "caseList", "caseDetail"];
/** Blocks the portal may show: applicants only start services and follow their own cases. */
export const PORTAL_BLOCK_TYPES: ScreenBlockType[] = ["heading", "text", "serviceStart", "caseList"];

export interface ScreenDefinitionView {
  key: string; version: number; name: string; nameAr?: string | null; description?: string | null; descriptionAr?: string | null;
  surface: "backoffice" | "portal" | string; kind: "list" | "detail" | "dashboard" | "form" | string;
  recordTypeKey?: string | null; serviceKey?: string | null; blocks: ScreenBlock[]; showInMenu: boolean; menuIcon?: string | null; menuOrder: number;
}

export const pick = (locale: Locale, en: string | null | undefined, ar: string | null | undefined): string => (locale === "ar" && ar ? ar : en ?? "");
